/**
 * The HTML overlay.
 *
 * No framework: index.html already ships every element and every keyframe, so
 * this file is only a typed driver over ids that must exist. Nothing here reads
 * game state — the loop pushes, the DOM listens.
 *
 * Two habits worth keeping. First, every write goes through textContent, never
 * innerHTML: golfer names and grade stamps are game text and must never be able
 * to reach the parser. Second, the per-frame setters (sun, hp, hint, distance)
 * cache their last value and skip the assignment when nothing changed, because
 * a style write on a paused string still costs a recalc.
 */

import { CRACKED_AT, HP_MAX, MAX_ANGER, SCUFFED_AT } from './tuning'

type CardOptions = {
	title: string
	sub: string
	stats: [string, string][]
	button: string
	onClick: () => void
	/** Optional second ending. Shown only when both label and handler are given. */
	secondary?: string
	onSecondary?: () => void
}

/** Everything the card shows: this hole, the round behind it, and the wind. */
export type ScoreView = {
	holeNo: number
	holeName: string
	par: number
	/** Strokes played on this hole so far. */
	strokes: number
	/** Metres from the ball to the flag. */
	toPin: number
	/** Finished holes: par and strokes for each. */
	card: { par: number; strokes: number }[]
	wind: { speed: number; dirDeg: number }
}

/** A golfer as the ball sees them from the grass: pips, not numbers. */
export type LobbyView = {
	name: string
	/** Each 0..5. `hot` draws the row in the flag colour — used for temper. */
	pips: { label: string; value: number; hot?: boolean }[]
	tempo: string
	queued: string[]
}

/** The windiest hole on the card blows this hard; the arrow is sized against it. */
const WIND_FULL = 5

/** Golf's own notation: level par is E, everything else keeps its sign. */
function toPar(n: number): string {
	if (n === 0) return 'E'
	return n > 0 ? `+${n}` : `${n}`
}

function overPar(card: ScoreView['card']): number {
	let n = 0
	for (const h of card) n += h.strokes - h.par
	return n
}

function req<T extends HTMLElement = HTMLElement>(id: string): T {
	const el = document.getElementById(id)
	if (!el) throw new Error(`Ui: index.html is missing #${id}`)
	return el as T
}

/**
 * Restart a CSS animation on an element that never left the DOM. Removing and
 * re-adding the class in one task coalesces into no change at all, so a layout
 * read has to happen in between — a repeated SHANK has to slam again.
 */
function retrigger(el: HTMLElement): void {
	el.classList.remove('show')
	el.getBoundingClientRect()
	el.classList.add('show')
}

export class Ui {
	private readonly sunRow: HTMLElement
	private readonly anger: HTMLElement
	private readonly hpFill: HTMLElement
	private readonly best: HTMLElement
	private readonly stamp: HTMLElement
	private readonly substamp: HTMLElement
	private readonly distance: HTMLElement
	private readonly hint: HTMLElement
	private readonly debug: HTMLElement
	private readonly card: HTMLElement
	private readonly cardTitle: HTMLElement
	private readonly cardSub: HTMLElement
	private readonly cardStats: HTMLElement
	private readonly cardBtn: HTMLButtonElement
	private readonly cardBtn2: HTMLButtonElement
	private readonly lobby: HTMLElement
	private readonly lobbyName: HTMLElement
	private readonly lobbyPips: HTMLElement
	private readonly lobbyTempo: HTMLElement
	private readonly lobbyQueue: HTMLElement
	private readonly lobbyHoldFill: HTMLElement
	private readonly bark: HTMLElement
	private readonly score: HTMLElement
	private readonly scoreHole: HTMLElement
	private readonly scoreName: HTMLElement
	private readonly scorePar: HTMLElement
	private readonly scoreStrokes: HTMLElement
	private readonly scorePin: HTMLElement
	private readonly scoreTotal: HTMLElement
	private readonly wind: HTMLElement
	private readonly windArrow: HTMLElement
	private readonly windSpeed: HTMLElement

	private notches: HTMLElement[] = []
	private sunTotal = -1
	private hpWidth = ''
	private hpColour = ''
	private angerTier = Number.NaN
	private angerName = ''
	private bestText = ''
	private hintText = ''
	private lobbyKey = ''
	private barkTimer = 0
	private holdWidth = ''
	private debugText = ''
	private distanceText = ''
	private distancePeak = 0
	private scoreHead = ''
	private strokesText = ''
	private pinText = ''
	private toParText = ''
	private windKey = ''
	/** Swapped on every showCard, so the one permanent listener can never stack. */
	private cardAction: (() => void) | null = null
	private cardAction2: (() => void) | null = null

	constructor() {
		this.sunRow = req('sun-row')
		this.anger = req('anger')
		this.hpFill = req('hp-fill')
		this.best = req('best')
		this.stamp = req('stamp')
		this.substamp = req('substamp')
		this.distance = req('distance')
		this.hint = req('hint')
		this.debug = req('debug')
		this.card = req('card')
		this.cardTitle = req('card-title')
		this.cardSub = req('card-sub')
		this.cardStats = req('card-stats')
		this.cardBtn = req<HTMLButtonElement>('card-btn')
		this.cardBtn2 = req<HTMLButtonElement>('card-btn2')
		this.lobby = req('lobby')
		this.lobbyName = req('lobby-name')
		this.lobbyPips = req('lobby-pips')
		this.lobbyTempo = req('lobby-tempo')
		this.lobbyQueue = req('lobby-queue')
		this.lobbyHoldFill = req('lobby-hold-fill')
		this.bark = req('bark')
		this.score = req('score')
		this.scoreHole = req('score-hole')
		this.scoreName = req('score-name')
		this.scorePar = req('score-par')
		this.scoreStrokes = req('score-strokes')
		this.scorePin = req('score-pin')
		this.scoreTotal = req('score-total')
		this.wind = req('score-wind')
		this.windArrow = req('score-arrow')
		this.windSpeed = req('score-wind-speed')

		this.card.setAttribute('role', 'dialog')
		this.card.setAttribute('aria-modal', 'true')
		this.card.setAttribute('aria-labelledby', 'card-title')
		this.card.setAttribute('aria-hidden', 'true')
		this.card.tabIndex = -1
		this.cardBtn.addEventListener('click', () => {
			this.cardAction?.()
		})
		this.cardBtn2.addEventListener('click', () => {
			this.cardAction2?.()
		})
	}

	/**
	 * Somebody says something. `from` picks which corner it comes from: golfers
	 * speak over their own shoulder, the other balls speak from down in the
	 * bucket. The player-ball has no call here and never will.
	 */
	showBark(text: string, from: 'golfer' | 'bucket' = 'golfer'): void {
		if (!text) {
			this.hideBark()
			return
		}
		this.bark.textContent = text
		this.bark.classList.toggle('bucket', from === 'bucket')
		this.bark.classList.add('show')
		window.clearTimeout(this.barkTimer)
		// Long enough to read, short enough that it never becomes furniture.
		this.barkTimer = window.setTimeout(() => this.bark.classList.remove('show'), 2400)
	}

	hideBark(): void {
		window.clearTimeout(this.barkTimer)
		this.bark.classList.remove('show')
	}

	/**
	 * The bucket. Rebuilt only when the queue actually changes, since this sits
	 * on screen for as long as the player is deciding.
	 */
	showLobby(v: LobbyView): void {
		this.lobby.classList.add('show')
		const key = `${v.name}|${v.pips.map((p) => `${p.label}${p.value}`).join()}|${v.tempo}|${v.queued.join()}`
		if (key === this.lobbyKey) return
		this.lobbyKey = key

		this.lobbyName.textContent = v.name
		this.lobbyTempo.textContent = `tempo ${v.tempo}`

		this.lobbyPips.replaceChildren()
		for (const p of v.pips) {
			const label = document.createElement('span')
			label.textContent = p.label
			const pips = document.createElement('span')
			pips.className = 'pips'
			for (let i = 0; i < 5; i++) {
				const dot = document.createElement('i')
				dot.className = `pip${i < p.value ? ' on' : ''}${i < p.value && p.hot ? ' hot' : ''}`
				pips.appendChild(dot)
			}
			this.lobbyPips.append(label, pips)
		}

		this.lobbyQueue.replaceChildren()
		for (const name of v.queued) {
			const el = document.createElement('div')
			el.className = 'queued'
			const b = document.createElement('b')
			b.textContent = name
			el.append('next', b)
			this.lobbyQueue.appendChild(el)
		}
	}

	/** 0..1 progress toward rolling back into the bucket. */
	setLobbyHold(p: number): void {
		const w = `${Math.round(Math.min(1, Math.max(0, p)) * 100)}%`
		if (w === this.holdWidth) return
		this.holdWidth = w
		this.lobbyHoldFill.style.width = w
	}

	hideLobby(): void {
		this.lobby.classList.remove('show')
		this.lobbyKey = ''
		this.setLobbyHold(0)
	}

	/**
	 * The daylight row. Burnt notches light up; once golden hour starts, what is
	 * left of the day turns the flag colour so the pressure is visible at a glance.
	 */
	setSun(used: number, total: number, golden: boolean): void {
		const count = Math.max(0, Math.round(total))
		if (count !== this.sunTotal) {
			this.sunTotal = count
			this.notches = []
			const frag = document.createDocumentFragment()
			for (let i = 0; i < count; i++) {
				const notch = document.createElement('div')
				notch.className = 'notch'
				this.notches.push(notch)
				frag.appendChild(notch)
			}
			this.sunRow.replaceChildren(frag)
		}
		const spent = Math.max(0, Math.min(count, Math.round(used)))
		for (const [i, notch] of this.notches.entries()) {
			let cls = 'notch'
			if (i < spent) cls = 'notch lit'
			else if (golden) cls = 'notch golden'
			if (notch.className !== cls) notch.className = cls
		}
	}

	/** hp01 is 0..1; the fill also changes colour at the scuffed and cracked marks. */
	setHp(hp01: number): void {
		const frac = Math.max(0, Math.min(1, hp01))
		const width = `${(frac * 100).toFixed(1)}%`
		if (width !== this.hpWidth) {
			this.hpWidth = width
			this.hpFill.style.width = width
		}
		const hp = frac * HP_MAX
		let colour = 'var(--cream)'
		if (hp <= CRACKED_AT) colour = 'var(--flag)'
		else if (hp <= SCUFFED_AT) colour = 'var(--sand)'
		if (colour !== this.hpColour) {
			this.hpColour = colour
			this.hpFill.style.background = colour
		}
	}

	setAnger(tier: number, name: string): void {
		if (tier === this.angerTier && name === this.angerName) return
		this.angerTier = tier
		this.angerName = name
		this.anger.textContent = name
		const heat = Math.max(0, Math.min(1, tier / MAX_ANGER))
		this.anger.style.color = tier > 0 ? 'var(--flag)' : ''
		this.anger.style.opacity = (0.55 + 0.45 * heat).toFixed(2)
	}

	/** The letterpress grade stamp: 'PURE!', 'chunked.', 'SHANK'. */
	showStamp(main: string, sub?: string): void {
		this.stamp.textContent = main
		retrigger(this.stamp)
		if (sub) {
			this.substamp.textContent = sub
			retrigger(this.substamp)
		} else {
			this.substamp.classList.remove('show')
			this.substamp.textContent = ''
		}
	}

	/** The settled number. Also pins the ratchet so a late tick cannot undo it. */
	showDistance(metres: number): void {
		this.distancePeak = metres
		this.writeDistance(metres)
		this.distance.classList.add('show')
	}

	hideDistance(): void {
		this.distance.classList.remove('show')
		this.distancePeak = 0
	}

	/**
	 * Per-frame during flight. The readout ratchets: it climbs with the ball and
	 * never counts backwards off a bounce, which is what sells it as an animation
	 * rather than a live variable. Tabular figures come from #distance's CSS.
	 */
	tickDistance(metres: number): void {
		this.distancePeak = Math.max(this.distancePeak, metres)
		this.writeDistance(this.distancePeak)
		this.distance.classList.add('show')
	}

	private writeDistance(metres: number): void {
		const text = `${Math.round(Math.max(0, metres))} m`
		if (text === this.distanceText) return
		this.distanceText = text
		this.distance.textContent = text
	}

	/** The diegetic onboarding line, e.g. 'HOLD to duck · RELEASE to be brave'. */
	setHint(text: string): void {
		if (text === this.hintText) return
		this.hintText = text
		this.hint.textContent = text
	}

	setBest(metres: number): void {
		const text = metres > 0 ? `Best ${Math.round(metres)} m` : ''
		if (text === this.bestText) return
		this.bestText = text
		this.best.textContent = text
	}

	/**
	 * The card: which hole, what it costs, how far is left, and where the round
	 * stands. Pushed every frame while the ball is in play, so like the other
	 * per-frame setters each field is compared before it is written.
	 *
	 * The running total counts finished holes only, the way a leaderboard does —
	 * the hole you are lying on cannot be over par until you have holed out.
	 */
	setScore(v: ScoreView): void {
		this.score.classList.add('show')

		const head = `${v.holeNo}|${v.holeName}|${v.par}`
		if (head !== this.scoreHead) {
			this.scoreHead = head
			this.scoreHole.textContent = `Hole ${v.holeNo}`
			this.scoreName.textContent = v.holeName
			this.scorePar.textContent = `Par ${v.par}`
		}

		const strokes = `${Math.max(0, Math.round(v.strokes))}`
		if (strokes !== this.strokesText) {
			this.strokesText = strokes
			this.scoreStrokes.textContent = strokes
		}

		const pin = `${Math.round(Math.max(0, v.toPin))} m`
		if (pin !== this.pinText) {
			this.pinText = pin
			this.scorePin.textContent = pin
		}

		const total = toPar(overPar(v.card))
		if (total !== this.toParText) {
			this.toParText = total
			this.scoreTotal.textContent = total
		}

		this.setWind(v.wind.speed, v.wind.dirDeg)
	}

	hideScore(): void {
		this.score.classList.remove('show')
	}

	/**
	 * The arrow points where the wind is going — 0deg is straight downrange —
	 * and both grows and darkens with speed. A dead calm shows nothing rather
	 * than an arrow pointing nowhere in particular.
	 */
	private setWind(speed: number, dirDeg: number): void {
		const key = `${speed.toFixed(1)}|${Math.round(dirDeg)}`
		if (key === this.windKey) return
		this.windKey = key
		if (speed <= 0) {
			this.wind.classList.remove('show')
			return
		}
		const t = Math.min(1, speed / WIND_FULL)
		this.windArrow.style.transform = `rotate(${Math.round(dirDeg)}deg) scale(${(0.78 + 0.42 * t).toFixed(2)})`
		this.windArrow.style.opacity = (0.5 + 0.5 * t).toFixed(2)
		this.windSpeed.textContent = `${speed.toFixed(1)} m/s`
		this.wind.classList.add('show')
	}

	/**
	 * The round, hole by hole, through the card that already exists. Rows are in
	 * play order, so the first one is hole one.
	 */
	showCardSummary(o: {
		title: string
		sub: string
		card: ScoreView['card']
		button: string
		onClick: () => void
	}): void {
		const stats: [string, string][] = o.card.map((h, i) => [
			`Hole ${i + 1} · par ${h.par}`,
			`${h.strokes} (${toPar(h.strokes - h.par)})`,
		])
		stats.push(['Round', toPar(overPar(o.card))])
		this.showCard({ title: o.title, sub: o.sub, stats, button: o.button, onClick: o.onClick })
	}

	showCard(o: CardOptions): void {
		this.cardTitle.textContent = o.title
		this.cardSub.textContent = o.sub
		const frag = document.createDocumentFragment()
		for (const [label, value] of o.stats) {
			const dt = document.createElement('dt')
			dt.textContent = label
			const dd = document.createElement('dd')
			dd.textContent = value
			frag.appendChild(dt)
			frag.appendChild(dd)
		}
		this.cardStats.replaceChildren(frag)
		this.cardBtn.textContent = o.button
		this.cardAction = o.onClick
		const hasSecond = Boolean(o.secondary && o.onSecondary)
		this.cardBtn2.hidden = !hasSecond
		this.cardBtn2.textContent = o.secondary ?? ''
		this.cardAction2 = hasSecond ? (o.onSecondary ?? null) : null
		this.card.setAttribute('aria-hidden', 'false')
		this.card.classList.add('show')
		// Flush the display change before focusing: an element still computed as
		// display:none is not focusable, and focus() would silently do nothing.
		this.card.getBoundingClientRect()
		this.cardBtn.focus()
	}

	hideCard(): void {
		this.card.classList.remove('show')
		this.card.setAttribute('aria-hidden', 'true')
		this.cardAction = null
		this.cardAction2 = null
		this.cardBtn.blur()
		this.cardBtn2.blur()
	}

	setDebug(lines: string[] | null): void {
		if (!lines || lines.length === 0) {
			this.debug.classList.remove('show')
			this.debug.textContent = ''
			this.debugText = ''
			return
		}
		const text = lines.join('\n')
		if (text !== this.debugText) {
			this.debugText = text
			this.debug.textContent = text
		}
		this.debug.classList.add('show')
	}
}
